import { useEffect, useMemo, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Flame, Gauge, Beef, Wheat, Droplet } from "lucide-react";
import SectionHeading from "./SectionHeading";

const ACTIVITIES = {
  Sedentary: { factor: 1.2, hint: "Desk-bound, minimal exercise" },
  "Lightly Active": { factor: 1.375, hint: "Light exercise 1–3 days/week" },
  "Moderately Active": { factor: 1.55, hint: "Training 3–5 days/week" },
  "Very Active": { factor: 1.725, hint: "Training 6–7 days/week" },
};

const GOAL_ADJ = { "Fat Loss": -0.2, "Muscle Gain": 0.15, Recomposition: 0 };
const PROTEIN_PER_KG = { "Fat Loss": 2.2, "Muscle Gain": 2.0, Recomposition: 2.2 };

function AnimatedNumber({ value, testId }) {
  const spring = useSpring(0, { stiffness: 90, damping: 24 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const text = useTransform(spring, (v) => Math.round(v).toLocaleString("en-IN"));
  return <motion.span data-testid={testId}>{text}</motion.span>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl bg-ink border border-line px-4 py-3 text-white font-mono focus:border-accent focus:outline-none transition-colors";

export default function Calculator() {
  const [ageInput, setAgeInput] = useState("28");
  const [gender, setGender] = useState("Male");
  const [height, setHeight] = useState(175);
  const [weight, setWeight] = useState(80);
  const [activity, setActivity] = useState("Moderately Active");
  const [goal, setGoal] = useState("Fat Loss");

  const age = Math.min(90, Math.max(10, parseInt(ageInput, 10) || 10));

  const r = useMemo(() => {
    const bmr = 10 * weight + 6.25 * height - 5 * age + (gender === "Male" ? 5 : -161);
    const maintenance = bmr * ACTIVITIES[activity].factor;
    const target = maintenance * (1 + GOAL_ADJ[goal]);
    const proteinG = weight * PROTEIN_PER_KG[goal];
    const fatG = (target * 0.25) / 9;
    const carbG = Math.max((target - proteinG * 4 - fatG * 9) / 9, 0);
    return {
      bmr,
      maintenance,
      target,
      protein: Math.round(proteinG),
      carbs: Math.round(carbG),
      fats: Math.round(fatG),
    };
  }, [age, gender, height, weight, activity, goal]);

  const macroPct = (grams, calsPerG) =>
    Math.round(((grams * calsPerG) / r.target) * 100);

  return (
    <section id="tdee-calculator" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="TDEE Calculator"
          title="Know Your Numbers"
          sub="Estimate your maintenance calories and daily macro targets in seconds — the same Mifflin-St Jeor science behind every custom plan."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-start">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 rounded-3xl border border-line bg-panel p-8 space-y-7"
            data-testid="calculator-inputs"
          >
            <Field label="Age">
              <input
                type="text"
                inputMode="numeric"
                value={ageInput}
                onChange={(e) => setAgeInput(e.target.value.replace(/\D/g, "").slice(0, 2))}
                onBlur={() => setAgeInput(String(age))}
                placeholder="28"
                className={inputCls}
                data-testid="tdee-age-input"
              />
            </Field>
            <Field label="Gender">
              <div className="grid grid-cols-2 gap-2">
                {["Male", "Female"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGender(g)}
                    data-testid={`tdee-gender-${g.toLowerCase()}`}
                    className={`py-3 rounded-xl font-semibold text-sm transition-all duration-300 border ${
                      gender === g
                        ? "bg-accent text-ink border-accent shadow-glow-emerald"
                        : "bg-ink text-slate-300 border-line hover:border-accent/50"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={`Height — ${height} cm`}>
              <input
                type="range"
                min="140"
                max="210"
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full"
                data-testid="tdee-height-slider"
              />
            </Field>
            <Field label={`Weight — ${weight} kg`}>
              <input
                type="range"
                min="40"
                max="160"
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value))}
                className="w-full"
                data-testid="tdee-weight-slider"
              />
            </Field>
            <Field label="Activity Level">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(ACTIVITIES).map(([name, a]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setActivity(name)}
                    data-testid={`tdee-activity-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-xl border p-3 text-left transition-all duration-300 ${
                      activity === name
                        ? "bg-accent/10 border-accent text-white"
                        : "bg-ink border-line text-slate-300 hover:border-accent/50"
                    }`}
                  >
                    <span className="block text-sm font-semibold">{name}</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">{a.hint}</span>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Primary Goal">
              <div className="grid grid-cols-3 gap-2">
                {Object.keys(GOAL_ADJ).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGoal(g)}
                    data-testid={`tdee-goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`py-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all duration-300 border ${
                      goal === g
                        ? "bg-gold text-ink border-gold shadow-glow-gold"
                        : "bg-ink text-slate-300 border-line hover:border-gold/50"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </Field>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="lg:col-span-7 space-y-6"
          >
            <div className="rounded-3xl border border-accent/40 bg-gradient-to-br from-panel to-ink p-8 lg:p-10 relative overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/15 blur-3xl"
              />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400">
                Target Daily Calories · {goal}
              </p>
              <p className="mt-3 font-display text-6xl lg:text-7xl font-black text-white leading-none">
                <AnimatedNumber value={r.target} testId="tdee-target-calories" />
                <span className="text-xl text-slate-500 font-mono ml-3">kcal</span>
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-ink/70 border border-line p-5" data-testid="tdee-bmr-card">
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    <Flame className="h-3.5 w-3.5 text-gold" /> BMR
                  </p>
                  <p className="mt-2 font-mono text-2xl font-bold text-white">
                    <AnimatedNumber value={r.bmr} testId="tdee-bmr-value" /> <span className="text-xs text-slate-500">kcal</span>
                  </p>
                </div>
                <div className="rounded-2xl bg-ink/70 border border-line p-5" data-testid="tdee-maintenance-card">
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    <Gauge className="h-3.5 w-3.5 text-emerald-400" /> Maintenance
                  </p>
                  <p className="mt-2 font-mono text-2xl font-bold text-white">
                    <AnimatedNumber value={r.maintenance} testId="tdee-maintenance-value" />{" "}
                    <span className="text-xs text-slate-500">kcal</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: Beef, label: "Protein", grams: r.protein, pct: macroPct(r.protein, 4), color: "bg-accent", id: "protein" },
                { icon: Wheat, label: "Carbs", grams: r.carbs, pct: macroPct(r.carbs, 4), color: "bg-gold", id: "carbs" },
                { icon: Droplet, label: "Fats", grams: r.fats, pct: macroPct(r.fats, 9), color: "bg-slate-400", id: "fats" },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded-3xl border border-line bg-panel p-6 transition-all duration-300 hover:border-accent/40"
                  data-testid={`tdee-macro-${m.id}`}
                >
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    <m.icon className="h-3.5 w-3.5 text-emerald-400" /> {m.label}
                  </p>
                  <p className="mt-3 font-mono text-3xl font-bold text-white">
                    <AnimatedNumber value={m.grams} testId={`tdee-macro-${m.id}-grams`} />
                    <span className="text-sm text-slate-500"> g</span>
                  </p>
                  <div className="mt-4 h-1.5 rounded-full bg-ink overflow-hidden">
                    <motion.div
                      className={`h-full ${m.color}`}
                      animate={{ width: `${Math.min(m.pct, 100)}%` }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  </div>
                  <p className="mt-2 font-mono text-[11px] text-slate-500">{m.pct}% of calories</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}